'use client';

import { useCallback, useEffect } from 'react';

import { hasModalQuery } from '@/components/modal/url-query';
import { userUIStore } from '@/stores/userUIStore';

import { useUrlParams } from './useUrlParams';
import { ToastMessages } from './interfaces';

export const useToastNotifications = (
  messagesLimits: ToastMessages,
  textVerified: ToastMessages,
) => {
  const { setAlert } = userUIStore();
  const { cont, emailVerification, urlBonusParam } = useUrlParams();

  const showLimitMessage = useCallback(() => {
    if (cont && messagesLimits[Number(cont)]) {
      setAlert({
        message: messagesLimits[Number(cont)],
        type: 'warning',
        show: true,
        duration: 5000,
      });
    }
  }, [cont, messagesLimits, setAlert]);

  const showEmailVerificationMessage = useCallback(() => {
    if (emailVerification) {
      const isSuccess = emailVerification === '1';
      setAlert({
        message: textVerified[Number(emailVerification)],
        type: isSuccess ? 'success' : 'error',
        show: true,
        duration: 4000,
      });
    }
  }, [emailVerification, textVerified, setAlert]);

  const cleanUrlIfNeeded = useCallback(() => {
    // `?modal=wallet` is the open state of a URL modal, not a one-shot toast
    // param. The old wipe replaced the whole query with `/` and left the
    // modal open against a clean address bar.
    if (hasModalQuery(window.location.search)) return;

    if (
      (!urlBonusParam || urlBonusParam !== process.env.NEXT_PUBLIC_URL_VAR_BONUS_PARAM_VALUE)
      && !emailVerification
    ) {
      window.history.replaceState({}, '', '/');
    }
  }, [urlBonusParam, emailVerification]);

  // single useEffect to handle all notifications
  useEffect(() => {
    showLimitMessage();
    showEmailVerificationMessage();
    cleanUrlIfNeeded();
  }, [showLimitMessage, showEmailVerificationMessage, cleanUrlIfNeeded]);

  return {
    showLimitMessage,
    showEmailVerificationMessage,
    cleanUrlIfNeeded,
  };
};
